'use client'

import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, type ReactNode } from 'react'

/**
 * SectionHeading
 * --------------
 * Drop-in case-study `<h2>` whose text color is driven by scroll progress.
 * As the heading travels from the bottom of the viewport up through the
 * top, its color follows a triangular keyframe:
 *
 *   charcoal-900  →  brand-500  →  charcoal-900
 *      (entering)     (centered)    (leaving)
 *
 * That gives the heading a smooth color "wash" as it passes the middle of
 * the screen and a clean fade back to the body color before it scrolls
 * off, in either scroll direction. Driven by `useScroll`/`useTransform`
 * (the same pattern Lenis already powers elsewhere on the site) so the
 * animation stays perfectly tied to the scroll position.
 */
export default function SectionHeading({
  children,
  className = 'font-display font-medium text-3xl md:text-4xl tracking-tight mb-6',
}: {
  children: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLHeadingElement>(null)
  // Track this heading's progress through the viewport. `start end` is the
  // moment its top edge enters the bottom of the viewport; `end start` is
  // the moment its bottom edge exits the top.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  // Triangular keyframe: charcoal → purple → charcoal. The 0.4 / 0.6
  // bracket holds the heading at brand-purple while it sits across the
  // viewport center, so the color reads as fully purple instead of just
  // touching it on its way past.
  const color = useTransform(
    scrollYProgress,
    [0, 0.4, 0.6, 1],
    ['#1a1a1a', '#7D40FF', '#7D40FF', '#1a1a1a'],
  )

  return (
    <motion.h2 ref={ref} className={className} style={{ color }}>
      <span>{children}</span>
    </motion.h2>
  )
}
